import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import Logos from "@/components/landing/Logos";
import Features from "@/components/landing/Features";
import Results from "@/components/landing/Results";
import Capabilities from "@/components/landing/Capabilities";
import Testimonials from "@/components/landing/Testimonials";
import Pricing from "@/components/landing/Pricing";
import TrustBadges from "@/components/landing/TrustBadges";
import FAQ from "@/components/landing/FAQ";
import CTASection from "@/components/landing/CTASection";
import Footer from "@/components/landing/Footer";
import JsonLd from "@/components/seo/JsonLd";
import { useEffect } from "react";
import { useLocale, detectLang } from "@/i18n/detect";
import { setSeoHead } from "@/lib/seoHead";

const homepageSchemas = [
  {
    "@type": "SoftwareApplication",
    "@id": "https://www.freecrm.com/#software",
    name: "FreeCRM",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web, iOS, Android",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
    publisher: { "@id": "https://www.freecrm.com/#organization" },
    url: "https://www.freecrm.com/",
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.8",
      reviewCount: "1200",
    },
  },
  {
    "@type": "FAQPage",
    "@id": "https://www.freecrm.com/#faq",
    publisher: { "@id": "https://www.freecrm.com/#organization" },
    mainEntity: [
      {
        "@type": "Question",
        name: "Is FreeCRM really free for email, voice, and SMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes — the Free plan includes email marketing, click-to-call, call logging, and 500 SMS messages per month at no cost. Paid plans unlock larger sending limits, advanced automation, call recording, and unlimited SMS.",
        },
      },
      {
        "@type": "Question",
        name: "How is FreeCRM different from HubSpot?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Unlike HubSpot, every channel is native — email marketing, voice, and SMS are all included, not sold as separate add-ons or costly integrations.",
        },
      },
      {
        "@type": "Question",
        name: "Can I run email campaigns and sequences?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Design broadcasts, drip campaigns, and multi-step sequences that mix email, SMS, and calls — with full open, click, and reply tracking.",
        },
      },
      {
        "@type": "Question",
        name: "Can I port my existing phone numbers?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Absolutely. You can bring your own numbers or purchase local numbers directly inside FreeCRM on Growth and Enterprise plans.",
        },
      },
      {
        "@type": "Question",
        name: "Do you support two-way SMS?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Send and receive SMS from the same inbox as your email, with templates, scheduled sends, and full conversation history per contact.",
        },
      },
    ],
  },
];

const Index = () => {
  const { ui } = useLocale();

  useEffect(() => {
    // English keeps the hand-tuned tags already in index.html; other locales
    // get a translated title/description so page titles are localized too.
    if (detectLang() === "en") return;
    setSeoHead({
      title: `${ui.hero.titleStart}${ui.hero.titleHighlight} — FreeCRM`,
      description: ui.hero.subtitle,
      path: "/",
    });
  }, [ui]);

  return (
    <div className="min-h-screen bg-background font-sans text-secondary">
      <JsonLd
        name="FreeCRM — The free CRM that grows with your business"
        description="FreeCRM is a powerful, free CRM for sales, marketing, and support teams. A modern alternative to HubSpot — no credit card, no contact limits."
        extra={homepageSchemas}
      />
      <Navbar />
      <main>
        <Hero />
        <Logos />
        <Features />
        <Results />
        <Capabilities />
        <Testimonials />
        <TrustBadges />
        <Pricing />
        <FAQ />
        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
